import React, { useState } from "react";
import api from "../lib/api";
import { Search, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function SerpAnalysis() {
  const [keyword, setKeyword] = useState("");
  const [location, setLocation] = useState("United Kingdom");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async (e) => {
    e?.preventDefault();
    if (!keyword.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/seo/serp", {
        keyword: keyword.trim(),
        location_name: location,
        language_name: "English",
      });
      setData(data.result);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "SERP fetch failed");
    } finally {
      setLoading(false);
    }
  };

  const items = (data?.items || []).filter((i) => i.type === "organic").slice(0, 20);

  return (
    <div className="space-y-5 pt-2" data-testid="serp-analysis-page">
      <div>
        <div className="text-[11px] uppercase tracking-[0.16em] text-[#a0aec0] font-semibold">
          Pages / Tools
        </div>
        <h1 className="font-display text-3xl mt-1 text-[#1a202c]">SERP analysis</h1>
        <p className="text-sm text-[#718096] mt-1.5">
          Inspect the live Google organic top 20 for any keyword.
        </p>
      </div>

      <form onSubmit={run} className="flex gap-2 flex-wrap">
        <div className="flex-1 min-w-[280px] relative">
          <Search className="h-3.5 w-3.5 absolute left-4 top-1/2 -translate-y-1/2 text-[#a0aec0]" />
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="enter a keyword…"
            data-testid="sa-keyword-input"
            className="w-full bg-white border border-[#e2e8f0] rounded-xl pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#4fd1c5] focus:ring-4 focus:ring-[#4fd1c5]/15 transition-all"
          />
        </div>
        <input
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          data-testid="sa-location-input"
          className="w-52 bg-white border border-[#e2e8f0] rounded-xl px-3.5 py-2.5 text-sm font-mono-tight focus:outline-none focus:border-[#4fd1c5] focus:ring-4 focus:ring-[#4fd1c5]/15 transition-all"
        />
        <button
          type="submit"
          disabled={loading || !keyword}
          data-testid="sa-search-button"
          className="btn-primary inline-flex items-center gap-2"
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
          Analyze
        </button>
      </form>

      {data && (
        <div className="card-soft overflow-hidden">
          <div className="px-5 py-4 border-b border-[#edf2f7] flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#a0aec0] font-semibold">results for</div>
              <h4 className="font-display text-base mt-0.5 text-[#1a202c]">{data.keyword}</h4>
            </div>
            <div className="text-xs text-[#718096] font-mono-tight">
              {data.se_results_count?.toLocaleString()} results
            </div>
          </div>
          <div className="divide-y divide-[#edf2f7] max-h-[640px] overflow-y-auto">
            {items.map((it, i) => (
              <div key={i} data-testid={`sa-row-${i}`} className="px-5 py-4 hover:bg-[#f7fafc]">
                <div className="flex items-start gap-3">
                  <div className="w-8 shrink-0 font-mono-tight text-[#a0aec0] text-sm">{it.rank_group}.</div>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-mono-tight text-[#718096] truncate">
                      {((it.url_from_breadcrumb && it.breadcrumb) || it.url || "").replace(/^https?:\/\//, "").slice(0, 90)}
                    </div>
                    <a href={it.goto_url || it.url} target="_blank" rel="noreferrer" className="text-sm font-semibold hover:text-[#2c7a7b] text-[#1a202c] block mt-0.5">
                      {it.title}
                    </a>
                    <div className="text-xs text-[#4a5568] mt-1 line-clamp-2">{it.description}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
